/** @jsx jsx */

/** eslint-disable react/sort-comp */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment } from 'react';

import type { Player } from 'video.js';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';

import { Typography } from '@coursera/cds-core';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import type TrackList from 'bundles/interactive-transcript/models/TrackList';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandCourseMaterialItemsV2 from 'bundles/naptimejs/resources/onDemandCourseMaterialItems.v2';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandVideosV1 from 'bundles/naptimejs/resources/onDemandVideos.v1';
import TrackedButton from 'bundles/page/components/TrackedButton';
import ShareButtonWithModal from 'bundles/sharing-common/components/modal/ShareButtonWithModal';
import ShareCTAV2 from 'bundles/sharing-common/components/modal/buttons/cds/ShareCTAV2';
import inServerContext from 'bundles/ssr/util/inServerContext';
import SharePreviewCard from 'bundles/video-logged-out-page/components/SharePreviewCard';
import ContentGateVideoOverlay from 'bundles/video-logged-out-page/components/content-gates/ContentGateVideoOverlay';
import NextVideoCounter from 'bundles/video-logged-out-page/components/video-player/NextVideoCounter';
import VideoJSPlayerWrapper, {
  type PlayerEventHandlers,
} from 'bundles/video-logged-out-page/components/video-player/VideoJSPlayerWrapper';
import { DEFAULT_VIDEO_WRAPPER_WIDTH } from 'bundles/video-logged-out-page/constants';
import VideoPlayerModel from 'bundles/video-logged-out-page/models/VideoPlayerModel';
import type { Partner, VLPItem } from 'bundles/video-logged-out-page/types/vlpSharedTypes';
import getSocialCaptions from 'bundles/video-logged-out-page/utils/getSocialCaptions';

import _t from 'i18n!nls/video-logged-out-page';

const videoStyles = {
  videoPlayerContainer: css`
    max-width: ${DEFAULT_VIDEO_WRAPPER_WIDTH}px;
    position: relative;
    padding-bottom: 5px;
    margin: 0 auto;
  `,

  videoPlayerContainerHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 25px 0 15px 0;
  `,
  videoPlayerContainerTitle: css`
    font-weight: bold;
    margin: 0;
    line-height: 36px;
  `,
  videoPlayerContainerVideoWrapper: css`
    position: relative;
    padding-top: 56.25%; /* video is 16:9 ratio, this will retain that height while video loads in */
    overflow: hidden;
    background: var(--cds-color-grey-975);
  `,

  loadingWrapper: css`
    position: absolute;
    top: calc(50% - 32px);
    left: calc(50% - 32px);
  `,

  videoOverlay: css`
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: radial-gradient(circle, rgb(8 39 78 / 70%) 0%, #08274e 100%);
  `,
  videoJs: css`
    font-size: 3em;
    line-height: 1.5em;
    width: 3em;
    display: block;
    position: absolute;
    cursor: pointer;
    opacity: 1;
    border: 0.06666em solid #fff; /* stylelint-disable-line number-max-precision */
    background-color: rgb(43 51 63 / 70%);
    transition: all 0.4s;
  `,

  videoCenter: css`
    display: block;
    vertical-align: top;
    box-sizing: border-box;
    color: #fff;
    background-color: #000;
    position: relative;
    padding: 0;
    font-size: 10px;
    line-height: 1;
    font-weight: 400;
    font-style: normal;
    font-family: Arial, Helvetica, sans-serif;
    word-break: normal;
  `,
  vjsBigPlayBbutton: css`
    border-radius: 50%;
    height: 3em;
    margin: 0;
    top: calc(50% - 1.5em);
    left: calc(50% - 1.5em);
  `,

  centerSpan: css`
    margin-left: 0.2em;
    margin-top: 0.2em;
    color: var(--cds-color-white-0);
  `,

  vjsControlText: css`
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  `,
  videoPlaceholder: css`
    width: 100%;
    max-height: 100%;
  `,
};

type BigPlayButtonProps = { onPlay: () => void };
type VideoPlaceholderProps = {
  src: string;
  onPlayVideo: () => void;
  showContentGateOverlay: boolean;
};

type Props = {
  courseId?: string;
  courseName?: string;
  partners?: Array<Partner>;
  onDemandVideos: OnDemandVideosV1;
  videoItem: OnDemandCourseMaterialItemsV2;
  videoIsReady: boolean;
  getVideoPlayerObject: (videojs: Player) => void;
  onPlayVideo: () => void;
  videoPlayer: Player | null;
  tracks: TrackList;
  nextItemObj?: VLPItem;
  showNextVideoCounter: boolean;
  showContentGateOverlay: boolean;
  getVideoTime: () => number;
  showPlaceholder?: boolean;
  playerEventHandlers?: Partial<PlayerEventHandlers>;
};

type PropsToComponent = Props;

const BigPlayButton = ({ onPlay }: BigPlayButtonProps) => {
  return (
    <div css={[videoStyles.videoCenter, videoStyles.videoOverlay]}>
      <TrackedButton
        css={[videoStyles.videoJs, videoStyles.vjsBigPlayBbutton]}
        type="button"
        aria-live="polite"
        onClick={onPlay}
        trackingName="click_play_video_button"
      >
        <span className="cif-play" css={videoStyles.centerSpan} />
        <span css={videoStyles.vjsControlText}>{_t('Play Video')}</span>
      </TrackedButton>
    </div>
  );
};

const LoaderSignalWithOverlay = () => (
  <div>
    <div css={videoStyles.videoOverlay} />
    <div css={videoStyles.loadingWrapper}>
      <SvgLoaderSignal size={64} />
    </div>
  </div>
);

const VideoPlaceholder = ({ src, onPlayVideo, showContentGateOverlay }: VideoPlaceholderProps) => (
  <div css={videoStyles.videoOverlay}>
    <ImgixWithPrioritizedLoading
      alt={_t('Video placeholder')}
      src={src}
      width={DEFAULT_VIDEO_WRAPPER_WIDTH}
      height={Math.floor((DEFAULT_VIDEO_WRAPPER_WIDTH / 16) * 9)}
      css={videoStyles.videoPlaceholder}
    />
    {!inServerContext ? (
      <div>{!showContentGateOverlay && <BigPlayButton onPlay={onPlayVideo} />}</div>
    ) : (
      <LoaderSignalWithOverlay />
    )}
  </div>
);

export class VideoPlayerContainer extends React.Component<PropsToComponent> {
  componentWillReceiveProps(nextProps: Props) {
    const { videoIsReady } = this.props;

    if (!videoIsReady && nextProps.videoIsReady && nextProps.videoPlayer) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'tracks' does not exist on type 'TrackLis... Remove this comment to see the full error message
      nextProps.tracks.tracks.forEach(({ label, languageCode, url }) => {
        nextProps.videoPlayer?.addRemoteTextTrack(
          {
            kind: 'subtitles',
            label,
            language: languageCode,
            src: url,
          },
          true
        );
      });
      nextProps.videoPlayer.play();
    }
  }

  render() {
    const {
      videoIsReady,
      getVideoPlayerObject,
      onDemandVideos,
      onPlayVideo,
      videoItem,
      nextItemObj,
      videoPlayer,
      showNextVideoCounter,
      showContentGateOverlay,
      courseName,
      partners,
      getVideoTime,
      showPlaceholder,
      playerEventHandlers: { onLoadedMetadata, onTimeUpdate, onDurationChange } = {},
    } = this.props;
    const partnerNames = partners && partners.map((partner) => partner.name).join(', ');
    const imageResolution = Object.keys(onDemandVideos?.sources?.byResolution ?? {}).find(
      (resolution) => resolution.toLowerCase() === '360p'
    );
    const screenshot = onDemandVideos?.sources?.byResolution?.[`${imageResolution}`]?.previewImageUrl ?? '';

    const videoModel = new VideoPlayerModel({ onDemandVideos });

    const showVideoPlacehodler = showPlaceholder ? (
      <VideoPlaceholder onPlayVideo={onPlayVideo} src={screenshot} showContentGateOverlay={showContentGateOverlay} />
    ) : (
      <Fragment>
        <VideoJSPlayerWrapper
          videoModel={videoModel}
          getPlayer={getVideoPlayerObject}
          onLoadedMetadata={onLoadedMetadata}
          onTimeUpdate={onTimeUpdate}
          onDurationChange={onDurationChange}
        />
        {!videoIsReady && (
          <div css={videoStyles.loadingWrapper}>
            <SvgLoaderSignal size={64} />
          </div>
        )}
      </Fragment>
    );

    return (
      <div css={videoStyles.videoPlayerContainer}>
        <div css={videoStyles.videoPlayerContainerHeader}>
          <Typography variant="h2semibold" css={videoStyles.videoPlayerContainerTitle}>
            {videoItem.name}
          </Typography>
          <ShareButtonWithModal
            tabbable={true}
            style={{ marginLeft: '10px' }}
            utmMediumParam="page_share"
            utmContentParam="vlp"
            utmCampaignParam="top_button"
            title={_t('Share this video')}
            description={_t('If you enjoy this video, take the time to show your friends.')}
            previewComponent={
              <SharePreviewCard title={videoItem.name} partnerName={partnerNames} imageSrc={screenshot} />
            }
            captions={getSocialCaptions({
              videoTitle: videoItem.name,
              courseName,
              partnerName: partnerNames,
            })}
            getVideoTimeInSeconds={getVideoTime}
          >
            <ShareCTAV2 />
          </ShareButtonWithModal>
        </div>
        <div css={videoStyles.videoPlayerContainerVideoWrapper}>
          {showContentGateOverlay && <ContentGateVideoOverlay />}
          {showVideoPlacehodler}
          {showNextVideoCounter && nextItemObj && (
            <NextVideoCounter
              nextItemObj={nextItemObj}
              videoPlayer={videoPlayer}
              currentItemTitle={videoItem.name}
              courseName={courseName}
              partnerNames={partnerNames}
              screenshot={screenshot}
              getVideoTime={getVideoTime}
            />
          )}
        </div>
      </div>
    );
  }
}

export default VideoPlayerContainer;
